---
title: 100x Developers Landing Page
---

<script lang="ts">
  import Card from '$lib/components/Card.svelte';
  import CardList from '$lib/components/CardList.svelte';
  import ConvertKitForm from '$lib/components/ConvertKitForm.svelte';

  export let data;

  let formElement: HTMLDivElement;
  let upgradeFormElement: HTMLDivElement;
  let showPreviewForm = false;
  let showPromptsPreview = false;
  let showUpgradePreview = false;

  function focusOnForm(): void {
    if (formElement) {
      // Add a small delay to ensure the form is fully rendered
      setTimeout(() => {
        // Find the first input element within the form and focus it
        const firstInput = formElement.querySelector('input[type="email"], input[type="text"]') as HTMLInputElement;
        if (firstInput) {
          firstInput.focus();
          firstInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else {
          // Fallback: scroll to the form element itself
          formElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    }
  }

  function togglePreviewForm(): void {
    showPreviewForm = !showPreviewForm;
    showPromptsPreview = showPreviewForm; // Also show prompts preview when form is shown
    if (showPreviewForm) {
      // Focus on form after it appears
      setTimeout(() => {
        focusOnForm();
      }, 100);
    }
  }

  function togglePromptsPreview(): void {
    showPromptsPreview = !showPromptsPreview;
  }

  function focusOnUpgradeForm(): void {
    if (upgradeFormElement) {
      // Add a small delay to ensure the form is fully rendered
      setTimeout(() => {
        // Find the first input element within the form and focus it
        const firstInput = upgradeFormElement.querySelector('input[type="email"], input[type="text"]') as HTMLInputElement;
        if (firstInput) {
          firstInput.focus();
          firstInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else {
          // Fallback: scroll to the form element itself
          upgradeFormElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    }
  }

  function toggleUpgradePreview(): void {
    showUpgradePreview = !showUpgradePreview;
    if (showUpgradePreview) {
      // Focus on form after it appears
      setTimeout(() => {
        focusOnUpgradeForm();
      }, 100);
    }
  }
</script>

<div class="override-landing">
<div class="container">
<!-- NEW HERO SECTION -->
<div class="hero">
    <div class="hero-content">
      <div class="hero-main">
        <div class="hero-left">
          <h1>The 100x Developer Playbook</h1>
          <p class="tagline">Proven AI workflows for developers. Get the Playbook that helps you code faster, ship with confidence, and future-proof your career.</p>

          <ul class="value-props">
            <li>Transform from code writer to systems architect using AI as your force multiplier</li>
            <li>Complete workflows for rapid prototyping, debugging, code review, and system design</li>
            <li>90-day action plan to integrate AI into your daily development process</li>
            <li>Learn the anti-patterns that make developers replaceable—and how to avoid them</li>
          </ul>
        </div>

        <div class="hero-right">
          <img src="/brands/100xdevelopers.com/images/100x-developer-playbook-cover-3d-tall.png" alt="The 100x Developer Playbook" class="book-mockup">
        </div>
      </div>

      <div class="cta-section">
        <div class="primary-cta">
          <a href="https://100xdevelopers.kit.com/products/the-100-x-developer-playbook" class="buy-button">Get The 100x Developer Playbook - $79</a>
        </div>

        <div class="hero-secondary-cta">
          <p>Not sure this will work for you?</p>
          {#if !showPreviewForm}
            <a href="#" on:click|preventDefault={togglePreviewForm} class="preview-link">Download a free preview →</a>
          {:else}
            <p class="preview-offer">Sample the methodology with 10 battle-tested prompts from the full Playbook:</p>
            <div bind:this={formElement}>
              <ConvertKitForm config={data.config} className="email-form" />
            </div>
            <p class="trust-line">No spam. Unsubscribe anytime.</p>
          {/if}
        </div>
      </div>
    </div>
</div>

{#if !showPromptsPreview}
  <div class="full-playbook-preview">
    <h2>What's Inside The 100x Developer Playbook</h2>
    <p style="text-align: center; margin-bottom: 30px; font-style: italic;">
        *The complete system for transforming from code writer to AI-powered systems architect*
    </p>

    <CardList listClass="playbook-features" itemClass="playbook-feature">
        - ### 1. The Complete 100x Developer Mindset
          Stop competing with AI and start leveraging it. Learn the four critical mindset shifts that separate developers who thrive from those who get left behind—from code monkey to architect, perfectionist to master editor, and individual contributor to force multiplier.

        - ### 2. 5 Production-Ready AI Workflows
          Battle-tested systems for rapid prototyping, test-driven development with AI, legacy code exploration, automated documentation, and refactoring. Each workflow includes exact prompts, real code examples, and step-by-step implementation guides you can use immediately.

        - ### 3. AI-Powered Code Review & Debugging
          Master the prompts that catch security vulnerabilities, enforce best practices, and debug cryptic errors in minutes instead of hours. Includes the "Security & Vulnerability Review" and "Find the Bug" frameworks used by senior developers.

        - ### 4. The Anti-Patterns That Get Developers Replaced
          Identify and avoid the four deadly mistakes that signal you're replaceable: blind copy-pasting, context-free prompting, using AI as a crutch, and stagnant learning. Learn exactly what separates a liability from an asset.

        - ### 5. 90-Day Transformation Action Plan
          A phased roadmap from foundation to mastery. Week-by-week checklist that takes you from AI novice to 100x Developer, with specific daily practices, milestones, and team leadership activities that prove your new expertise.

        - ### 6. Real-World Prompts for Every Development Task
          50+ copy-paste prompts covering code generation, debugging, refactoring, documentation, unit testing, architecture planning, framework learning, performance optimization, and security review—all tested in production environments.

        - ### 7. Future-Proof Your Career Strategy
          Understand why AI won't replace you, what makes you irreplaceable, and how to position yourself as the developer who uses AI to build what others can't. Based on industry data and proven by developers already making this transition.

        - ### 8. From Junior to Force Multiplier
          Whether you're entry-level or senior, learn how to use AI to accelerate your growth, mentor others, and multiply your team's impact. Includes strategies for teaching AI workflows to teammates and becoming the go-to expert in your organization.
    </CardList>
  </div>
{:else}
  <div class="prompts-preview">
    <h2>What's Inside Your Free Quickstart?</h2>
    <p style="text-align: center; margin-bottom: 30px; font-style: italic;">
        *These 10 AI prompts are carefully selected from "The 100x Developers Playbook" to give you an immediate productivity boost*
    </p>

    <CardList listClass="prompts" itemClass="prompt-item">
        - ### Prompt 1: Code Generation
          Quickly spin up full components, routes, or services in your favorite framework — with error handling and clean styling already baked in.

        - ### Prompt 2: Debugging Assistance
          Paste your error and code snippet, and let AI explain the root cause plus generate a fixed version line-by-line.

        - ### Prompt 3: Code Refactoring
          Transform messy code into clean, performant, standards-compliant functions with clear explanations of each improvement.

        - ### Prompt 4: Documentation Generation
          Generate professional JSDoc, Python docstrings, or other inline docs instantly — so your team (and future you) can read your code with ease.

        - ### Prompt 5: Unit Test Creation
          Produce robust test suites covering happy paths, edge cases, and errors in the framework of your choice.

        - ### Prompt 6: Code Review and Improvements
          Get structured feedback on bugs, efficiency, and best practices — plus corrected versions ready to drop in.

        - ### Prompt 7: Architecture Planning
          Ask for high-level design patterns and service diagrams that make your app scalable, modular, and easy to extend.

        - ### Prompt 8: Learn New Frameworks
          Get expert-style explanations of unfamiliar libraries or frameworks, tailored to what you already know (React, Vue, Rust, etc.).

        - ### Prompt 9: Performance Optimization
          Identify bottlenecks in queries or code, then get clear recommendations for faster execution and resource savings.

        - ### Prompt 10: Security Review
          Audit your code for vulnerabilities like SQL injection or XSS, and see how to patch them with secure best practices.
    </CardList>
  </div>
{/if}

  <div class="upgrade-section">
      <div class="upgrade-box">
          {#if showPromptsPreview}
              <h2>🚀 The Quickstart Guide is Just the Beginning</h2>
              <p>
              You've seen what 10 prompts can do. Now imagine having the complete system that's already helped developers transform their careers and multiply their impact.
              </p>
              <p>
              <strong>The 100x Developers Playbook</strong> contains everything you need:
              </p>
              <ul>
                  <li>The Complete 100x Developer Mindset - 4 critical mindset shifts</li>
                  <li>5 Production-Ready AI Workflows with exact prompts and examples</li>
                  <li>AI-Powered Code Review & Debugging frameworks</li>
                  <li>The Anti-Patterns That Get Developers Replaced</li>
                  <li>90-Day Transformation Action Plan with weekly checklists</li>
                  <li>50+ Real-World Prompts for Every Development Task</li>
                  <li>Future-Proof Your Career Strategy</li>
                  <li>From Junior to Force Multiplier - team leadership activities</li>
              </ul>
          {:else}
              <h2>🚀 Ready to Master the Complete System?</h2>
              <p>
              The 100x Developer Playbook gives you the complete transformation: 50+ production-ready AI prompts, 5 battle-tested workflows, the anti-patterns that get developers replaced, and a 90-day action plan to become indispensable.
              </p>
              <p>
              <strong>The 100x Developers Playbook</strong> contains:
              </p>
              <ul>
                  <li>Advanced AI prompts for every development scenario</li>
                  <li>Complete workflows for AI-powered development</li>
                  <li>Advanced debugging and optimization techniques</li>
                  <li>Team collaboration and code review systems</li>
                  <li>Future-proofing strategies for AI development</li>
              </ul>
          {/if}
           <div class="upgrade-ctas">
               <a href="https://100xdevelopers.kit.com/products/the-100-x-developer-playbook" class="buy-now-button">Get The Full Playbook - $79</a>
               <a href="/product/" class="learn-more-button">View Full Details</a>
           </div>

           <div class="upgrade-objection-handler">
               <a href="#" on:click|preventDefault={toggleUpgradePreview} class="upgrade-preview-link">Still not sure? Try the free preview first ↓</a>

               {#if showUpgradePreview}
                 <div class="upgrade-preview-form">
                   <h3>Get the 10-Prompt Quickstart Free</h3>
                   <p>Sample the methodology with 10 battle-tested prompts from Chapter 4. See how the system works before committing to the full Playbook.</p>
                   <div bind:this={upgradeFormElement}>
                     <ConvertKitForm config={data.config} className="upgrade-email-form" />
                   </div>
                 </div>
               {/if}
           </div>
       </div>
   </div>

  <div class="author">
      <h2>Meet the Author</h2>
      <h3>Brad Hefta-Gaub</h3>
      <p>Brad is a seasoned technology leader and developer who has built products at every scale — from startups to IPO. With decades of hands-on coding and team leadership, he's now focused on helping developers harness AI to work smarter, ship faster, and future-proof their careers.</p>
      <p style="margin-top: 15px; font-weight: bold;">Creator of "The 100x Developers Playbook" — the complete system for AI-powered development.</p>
  </div>

</div>
</div>

<style>
  :global(.override-landing) {
    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
    line-height: 1.6;
    color: #333;
    min-height: 100vh;
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  :global(.container) {
    max-width: 800px;
    margin: 0 auto;
    padding: 20px; /* Restore full padding for proper content spacing */
  }

  :global(.landing-content:has(.override-landing)) {
    max-width: 800px !important;
    font-size: inherit !important;
    line-height: inherit !important;
    margin: inherit !important;
    padding: 0 !important;
    text-align: inherit !important;
  }

  :global(.landing-hero:has(.override-landing)) {
    padding: 0 !important;
  }

  /* Remove fixed padding overrides - let layout handle dynamic spacing */
  :global(main:has(.override-landing)) {
    padding-left: 0 !important;
    padding-right: 0 !important;
    padding-bottom: 0 !important;
    margin: 0 !important;
  }

  :global(.landing-content:has(.override-landing) p) {
    font-size: inherit !important;
    margin-bottom: inherit !important;
    opacity: inherit !important;
    line-height: inherit !important;
  }

  /* NEW HERO SECTION STYLES */
  :global(.override-landing .hero) {
    background: linear-gradient(135deg, #2d1b4e 0%, #1a0f2e 100%);
    color: white;
    padding: 60px 20px;
    min-height: 90vh;
    display: flex;
    align-items: center;
    border-radius: 15px;
    box-shadow: 0 20px 40px rgba(0,0,0,0.1);
    margin-bottom: 30px;
  }

  :global(.override-landing .hero-content) {
    max-width: 800px;
    margin: 0 auto;
    width: 100%;
  }

  :global(.override-landing .hero-main) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 40px;
    align-items: center;
    margin-bottom: 40px;
  }

  :global(.override-landing .hero-left h1) {
    font-size: 3rem;
    font-weight: 700;
    line-height: 1.1;
    margin-bottom: 20px;
    color: white;
  }

  :global(.override-landing .hero-left .tagline) {
    font-size: 1.3rem;
    line-height: 1.5;
    margin-bottom: 35px;
    color: rgba(255,255,255,0.9);
  }

  :global(.override-landing .value-props) {
    list-style: none;
    margin-bottom: 40px;
    padding: 0;
  }

  :global(.override-landing .value-props li) {
    padding: 12px 0;
    padding-left: 35px;
    position: relative;
    font-size: 1.1rem;
    color: rgba(255,255,255,0.95);
  }

  :global(.override-landing .value-props li:before) {
    content: "✓";
    position: absolute;
    left: 0;
    color: #28a745;
    font-weight: bold;
    font-size: 1.3rem;
  }

  :global(.override-landing .cta-section) {
    background: rgba(255,255,255,0.1);
    backdrop-filter: blur(10px);
    border: 2px solid rgba(255,255,255,0.2);
    border-radius: 15px;
    padding: 30px;
    margin-bottom: 20px;
  }

  :global(.override-landing .primary-cta) {
    text-align: center;
    margin-bottom: 25px;
  }

  :global(.override-landing .buy-button) {
    background: linear-gradient(45deg, #28a745, #20c997);
    color: white;
    padding: 20px 40px;
    border: none;
    border-radius: 12px;
    font-size: 1.3rem;
    font-weight: bold;
    text-decoration: none;
    display: inline-block;
    transition: all 0.3s ease;
    box-shadow: 0 8px 25px rgba(40, 167, 69, 0.3);
  }

  :global(.override-landing .buy-button:hover) {
    transform: translateY(-3px);
    box-shadow: 0 15px 35px rgba(40, 167, 69, 0.4);
    color: white;
  }

  /* ConvertKit Form Styling */
  :global(.override-landing .email-form) {
    display: flex;
    gap: 10px;
    margin-bottom: 15px;
  }

  :global(.override-landing .email-form input[type="email"]) {
    flex: 1;
    padding: 16px 20px;
    border: 2px solid rgba(255,255,255,0.3);
    border-radius: 8px;
    font-size: 1rem;
    background: rgba(255,255,255,0.95);
    color: #333;
  }

  :global(.override-landing .email-form input[type="email"]::placeholder) {
    color: #6c757d;
  }

  :global(.override-landing .email-form button) {
    background: linear-gradient(45deg, #28a745, #20c997);
    color: white;
    padding: 16px 35px;
    border: none;
    border-radius: 8px;
    font-size: 1.1rem;
    font-weight: bold;
    cursor: pointer;
    transition: all 0.3s ease;
    white-space: nowrap;
  }

  :global(.override-landing .email-form button:hover) {
    transform: translateY(-2px);
    box-shadow: 0 10px 25px rgba(40, 167, 69, 0.4);
  }

  :global(.override-landing .trust-line) {
    font-size: 0.9rem;
    color: rgba(255,255,255,0.7);
    text-align: center;
  }

  :global(.override-landing .hero-secondary-cta) {
    text-align: center;
    padding-top: 20px;
    border-top: 1px solid rgba(255,255,255,0.2);
    margin-top: 20px;
  }

  :global(.override-landing .hero-secondary-cta p) {
    margin-bottom: 12px;
    color: rgba(255,255,255,0.9);
    font-size: 1rem;
  }

  :global(.override-landing .preview-link) {
    color: white;
    font-weight: bold;
    text-decoration: underline;
    font-size: 1.05rem;
    transition: all 0.3s ease;
  }

  :global(.override-landing .preview-link:hover) {
    color: #28a745;
  }

  :global(.override-landing .preview-offer) {
    color: rgba(255,255,255,0.8) !important;
    font-size: 0.95rem !important;
    margin-bottom: 20px !important;
  }

  :global(.override-landing .hero-right) {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  :global(.override-landing .book-mockup) {
    max-width: 400px;
    width: 100%;
    border-radius: 12px;
    box-shadow:
      0 20px 60px rgba(0,0,0,0.5),
      0 0 40px rgba(100, 200, 255, 0.2);
    transform: perspective(1000px) rotateY(-8deg);
    transition: transform 0.3s ease;
  }

  :global(.override-landing .book-mockup:hover) {
    transform: perspective(1000px) rotateY(0deg) scale(1.02);
  }

  /* ORIGINAL STYLES FOR REST OF PAGE */
  :global(.free-badge) {
      background: linear-gradient(45deg, #28a745, #20c997);
      color: white;
      padding: 8px 20px;
      border-radius: 25px;
      font-size: 14px;
      font-weight: bold;
      display: inline-block;
      margin-bottom: 20px;
      text-transform: uppercase;
      letter-spacing: 1px;
  }

  :global(.override-landing h1) {
      font-size: 2.5rem;
      color: #2c3e50;
      margin-bottom: 15px;
      font-weight: 700;
  }

  :global(.subtitle) {
      font-size: 1.2rem;
      color: #6c757d;
      margin-bottom: 25px;
  }

  :global(.main-offer) {
      background: #f8f9fa;
      border: 2px solid #28a745;
      border-radius: 10px;
      padding: 25px;
      margin: 30px 0;
  }

  :global(.main-offer h2) {
      color: #28a745;
      font-size: 1.5rem;
      margin-bottom: 15px;
  }

  :global(.main-offer p) {
      font-size: 1.1rem;
      margin-bottom: 10px;
  }

  :global(.cta-button-custom) {
      background: linear-gradient(45deg, #007bff, #0056b3);
      color: white;
      padding: 18px 40px;
      border: none;
      border-radius: 50px;
      font-size: 1.2rem;
      font-weight: bold;
      cursor: pointer;
      transition: all 0.3s ease;
      text-decoration: none;
      display: inline-block;
      margin: 20px 0;
      text-transform: uppercase;
      letter-spacing: 1px;
  }

  :global(.cta-button-custom:hover) {
      transform: translateY(-2px);
      box-shadow: 0 10px 20px rgba(0,123,255,0.3);
  }

  :global(.features) {
      margin: 30px 0;
  }

  /* Target the CardList structure */
  :global(.features[data-cardlist-scope] ul) {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
      gap: 20px;
      list-style: none;
      padding: 0;
      margin: 0;
  }

  :global(.features[data-cardlist-scope] li) {
      background: white;
      padding: 20px;
      border-radius: 10px;
      box-shadow: 0 5px 15px rgba(0,0,0,0.08);
  }

  :global(.features[data-cardlist-scope] li h3) {
      color: #007bff;
      margin-bottom: 10px;
  }

  :global(.full-playbook-preview) {
      background: white;
      border-radius: 15px;
      padding: 30px;
      margin: 30px 0;
      box-shadow: 0 10px 30px rgba(0,0,0,0.1);
  }

  :global(.full-playbook-preview h2) {
      text-align: center;
      color: #2c3e50;
      margin-bottom: 25px;
      font-size: 2rem;
  }

  :global(.prompts-preview) {
      background: white;
      border-radius: 15px;
      padding: 30px;
      margin: 30px 0;
      box-shadow: 0 10px 30px rgba(0,0,0,0.1);
  }

  :global(.prompts-preview h2) {
      text-align: center;
      color: #2c3e50;
      margin-bottom: 25px;
      font-size: 2rem;
  }

  /* Target the CardList structure for playbook features */
  :global(.playbook-features[data-cardlist-scope] ul) {
      list-style: none;
      padding: 0;
      margin: 0;
  }

  :global(.playbook-features[data-cardlist-scope] li) {
      border: none !important;
      border-left: 4px solid #28a745 !important;
      padding: 20px 25px;
      margin: 20px 0;
      background: #f8f9fa;
      border-radius: 0 8px 8px 0;
  }

  :global(.playbook-features[data-cardlist-scope] li h3) {
      color: #28a745;
      margin-bottom: 12px;
      font-size: 1.2rem;
  }

  :global(.playbook-features[data-cardlist-scope] li p) {
      color: #495057;
      line-height: 1.6;
  }

  /* Target the CardList structure for prompts */
  :global(.prompts[data-cardlist-scope] ul) {
      list-style: none;
      padding: 0;
      margin: 0;
  }

  :global(.prompts[data-cardlist-scope] li) {
      border: none !important; /* Override CardList fallback border */
      border-left: 4px solid #007bff !important; /* Ensure blue left border */
      padding: 15px 20px;
      margin: 15px 0;
      background: #f8f9fa;
      border-radius: 0 8px 8px 0;
  }

  /* More specific selector to ensure the border shows */
  :global(.prompts-preview .prompts[data-cardlist-scope] li) {
      border-left: 4px solid #007bff !important;
  }

  :global(.prompts[data-cardlist-scope] li h3) {
      color: #007bff;
      margin-bottom: 8px;
  }

  :global(.author) {
      background: white;
      border-radius: 15px;
      padding: 30px;
      margin: 30px 0;
      text-align: center;
      box-shadow: 0 10px 30px rgba(0,0,0,0.1);
  }

  :global(.author h2) {
      color: #2c3e50;
      margin-bottom: 15px;
  }


  /* Upgrade Section Styles - Scoped to prevent leaking */
  :global(.override-landing .upgrade-section) {
      background: linear-gradient(135deg, #6f42c1, #e83e8c) !important;
      border-radius: 15px !important;
      padding: 40px !important;
      margin: 40px 0 !important;
      color: white !important;
  }

  :global(.override-landing .upgrade-section .upgrade-box h2) {
      font-size: 2rem !important;
      margin-bottom: 20px !important;
      text-align: center !important;
      color: white !important;
  }

  :global(.override-landing .upgrade-section .upgrade-box p) {
      font-size: 1.1rem !important;
      margin-bottom: 20px !important;
      text-align: center !important;
      color: white !important;
  }

  :global(.override-landing .upgrade-section .upgrade-box ul) {
      max-width: 600px !important;
      margin: 0 auto 30px auto !important;
      padding-left: 20px !important;
  }

  :global(.override-landing .upgrade-section .upgrade-box li) {
      margin-bottom: 10px !important;
      font-size: 1rem !important;
      color: white !important;
      list-style: none !important;
      position: relative !important;
      padding-left: 25px !important;
  }

  :global(.override-landing .upgrade-section .upgrade-box li::before) {
      content: "✓" !important;
      color: white !important;
      font-weight: bold !important;
      position: absolute !important;
      left: 0 !important;
      font-size: 1.1rem !important;
  }

  :global(.override-landing .upgrade-section .upgrade-ctas) {
      text-align: center !important;
      margin-top: 30px !important;
  }

  :global(.override-landing .upgrade-section .buy-now-button) {
      background: linear-gradient(45deg, #28a745, #20c997) !important;
      color: white !important;
      padding: 15px 30px !important;
      border-radius: 30px !important;
      text-decoration: none !important;
      font-weight: bold !important;
      font-size: 1.1rem !important;
      margin-right: 20px !important;
      display: inline-block !important;
      margin-bottom: 15px !important;
      transition: all 0.3s ease !important;
      border: none !important;
  }

  :global(.override-landing .upgrade-section .buy-now-button:hover) {
      transform: translateY(-2px) !important;
      box-shadow: 0 10px 20px rgba(40, 167, 69, 0.3) !important;
      color: white !important;
  }

  :global(.override-landing .upgrade-section .learn-more-button) {
      color: white !important;
      border: 2px solid white !important;
      padding: 13px 25px !important;
      border-radius: 30px !important;
      text-decoration: none !important;
      font-weight: bold !important;
      display: inline-block !important;
      transition: all 0.3s ease !important;
      background: transparent !important;
  }

  :global(.override-landing .upgrade-section .learn-more-button:hover) {
      background: white !important;
      color: #6f42c1 !important;
  }

  /* Upgrade Objection Handler Styles */
  :global(.override-landing .upgrade-objection-handler) {
      text-align: center !important;
      margin-top: 30px !important;
      padding-top: 20px !important;
      border-top: 1px solid rgba(255,255,255,0.2) !important;
  }

  :global(.override-landing .upgrade-preview-link) {
      color: rgba(255,255,255,0.8) !important;
      text-decoration: underline !important;
      font-size: 0.9rem !important;
      transition: all 0.3s ease !important;
  }

  :global(.override-landing .upgrade-preview-link:hover) {
      color: white !important;
  }

  :global(.override-landing .upgrade-preview-form) {
      background: rgba(255,255,255,0.1) !important;
      border: 1px solid rgba(255,255,255,0.2) !important;
      border-radius: 10px !important;
      padding: 25px !important;
      margin-top: 20px !important;
      text-align: center !important;
  }

  :global(.override-landing .upgrade-preview-form h3) {
      color: white !important;
      font-size: 1.3rem !important;
      margin-bottom: 15px !important;
  }

  :global(.override-landing .upgrade-preview-form p) {
      color: rgba(255,255,255,0.9) !important;
      font-size: 1rem !important;
      margin-bottom: 20px !important;
  }

  /* Upgrade Email Form Styling */
  :global(.override-landing .upgrade-email-form) {
      display: flex;
      gap: 10px;
      margin-bottom: 15px;
  }

  :global(.override-landing .upgrade-email-form input[type="email"]) {
      flex: 1;
      padding: 14px 18px;
      border: 2px solid rgba(255,255,255,0.3);
      border-radius: 8px;
      font-size: 1rem;
      background: rgba(255,255,255,0.95);
      color: #333;
  }

  :global(.override-landing .upgrade-email-form input[type="email"]::placeholder) {
      color: #6c757d;
  }

  :global(.override-landing .upgrade-email-form button) {
      background: linear-gradient(45deg, #28a745, #20c997);
      color: white;
      padding: 14px 30px;
      border: none;
      border-radius: 8px;
      font-size: 1rem;
      font-weight: bold;
      cursor: pointer;
      transition: all 0.3s ease;
      white-space: nowrap;
  }

  :global(.override-landing .upgrade-email-form button:hover) {
      transform: translateY(-2px);
      box-shadow: 0 8px 20px rgba(40, 167, 69, 0.4);
  }

  /* Secondary and Tertiary CTA Styles - Scoped to prevent leaking */
  :global(.override-landing .secondary-cta) {
      background: #6c757d !important;
      color: white !important;
      padding: 12px 25px !important;
      border-radius: 25px !important;
      text-decoration: none !important;
      font-weight: bold !important;
      font-size: 0.9rem !important;
      transition: all 0.3s ease !important;
      display: inline-block !important;
  }

  :global(.override-landing .secondary-cta:hover) {
      background: #5a6268 !important;
      transform: translateY(-1px) !important;
      color: white !important;
  }

  :global(.override-landing .tertiary-cta) {
      color: #007bff !important;
      text-decoration: underline !important;
      font-weight: bold !important;
      transition: all 0.3s ease !important;
  }

  :global(.override-landing .tertiary-cta:hover) {
      color: #0056b3 !important;
  }

  @media (max-width: 768px) {
      :global(.container) {
          padding: 15px;
      }

      :global(.override-landing .hero) {
          padding: 25px;
      }

      :global(h1) {
          font-size: 2rem;
      }

      :global(.cta-button-custom) {
          padding: 15px 30px;
          font-size: 1.1rem;
      }


      /* Mobile responsive styles for upgrade section */
      :global(.override-landing .upgrade-section) {
          padding: 25px !important;
          margin: 20px 0 !important;
      }

      :global(.override-landing .upgrade-section .upgrade-box h2) {
          font-size: 1.5rem !important;
      }

      :global(.override-landing .upgrade-section .buy-now-button) {
          padding: 12px 25px !important;
          font-size: 1rem !important;
          margin-right: 0 !important;
          margin-bottom: 10px !important;
          display: block !important;
          width: 100% !important;
          box-sizing: border-box !important;
      }

      :global(.override-landing .upgrade-section .learn-more-button) {
          padding: 10px 20px !important;
          display: block !important;
          width: 100% !important;
          box-sizing: border-box !important;
      }

      /* Mobile responsive styles for secondary and tertiary CTAs */
      :global(.override-landing .secondary-cta) {
          padding: 10px 20px !important;
          font-size: 0.85rem !important;
          display: block !important;
          margin-bottom: 10px !important;
          text-align: center !important;
      }

      :global(.override-landing .tertiary-cta) {
          display: block !important;
          text-align: center !important;
          margin-top: 10px !important;
      }

      /* NEW HERO MOBILE STYLES */
      :global(.override-landing .hero-main) {
        grid-template-columns: 1fr;
        gap: 30px;
      }

      :global(.override-landing .hero-left h1) {
        font-size: 2.2rem;
      }

      :global(.override-landing .hero-left .tagline) {
        font-size: 1.1rem;
      }

      :global(.override-landing .buy-button) {
        padding: 18px 30px;
        font-size: 1.2rem;
        width: 100%;
        box-sizing: border-box;
      }

      :global(.override-landing .email-form) {
        flex-direction: column;
      }

      :global(.override-landing .email-form button) {
        width: 100%;
      }

      :global(.override-landing .book-mockup) {
        max-width: 300px;
      }

      /* Mobile responsive styles for upgrade objection handler */
      :global(.override-landing .upgrade-email-form) {
        flex-direction: column;
      }

      :global(.override-landing .upgrade-email-form button) {
        width: 100%;
      }
  }
</style>